import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

interface ServicesMobileProps {
  t: any;
  isRTL: boolean;
}

const ServicesSection = styled.section`
  padding: 4rem 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 100%;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 1rem;
  position: relative;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
  }
`;

const SectionSubtitle = styled(motion.h3)<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin-top: 1.5rem;
  line-height: 1.5;
`;

const ServicesGrid = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const ServiceCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  padding: 2rem 1.5rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
  
  &:active {
    transform: scale(0.98);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3);
  
  img {
    width: 40px;
    height: 40px;
    filter: brightness(0) invert(1);
  }
`;

const ServiceTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  line-height: 1.3;
`;

const ServiceDescription = styled.p<{ $isRTL: boolean }>`
  color: #6b7280;
  line-height: 1.6;
  font-size: 0.95rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin: 0;
`;

const ServicesMobile: React.FC<ServicesMobileProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    { 
      title: t.service2, 
      description: t.service2Desc,
      icon: "/images/services/installation.svg"
    },
    { 
      title: t.service4, 
      description: t.service4Desc,
      icon: "/images/services/solaire.svg"
    },
    { 
      title: t.service1, 
      description: t.service1Desc,
      icon: "/images/services/monitoring.svg"
    },
  ];

  return (
    <ServicesSection id="services-mobile" ref={ref}>
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {t.servicesTitle}
          </SectionTitle>
          <SectionSubtitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t.servicesSubtitle}
          </SectionSubtitle>
        </SectionHeader>

        <ServicesGrid $isRTL={isRTL}>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.5, 
                delay: 0.3 + index * 0.15
              }}
              onClick={() => {
                if (service.title === t.service2) {
                  navigate('/installation-service');
                } else if (service.title === t.service4) {
                  navigate('/solaire');
                } else if (service.title === t.service1) {
                  navigate('/monitoring');
                }
              }}
            >
              <ServiceIcon className="service-icon">
                <img src={service.icon} alt={service.title} />
              </ServiceIcon>
              <ServiceTitle $isRTL={isRTL}>{service.title}</ServiceTitle>
              <ServiceDescription $isRTL={isRTL}>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default ServicesMobile; 